class CartDrawerChanges {
    constructor() {
        this.observerTarget = document.querySelector('.quick-cart');
        this.observerConfig = { attributes: true, childList: true, subtree: true, characterData: true };
        this.RETURNS_FEE = 4.80;
        this.RETURNS_KEY = 'bh2o_returns_protection_enabled';
        this.GEO_KEY = 'bh2o_detected_country';
        this.GEO_TTL = 24 * 60 * 60 * 1000;
        this.detectedCountry = null;
        this.geoPending = false;
        this.geoChecked = false;
        this.observer = null;
        this.init();
    }

    getCartCount() {
        let count = 0;
        document.querySelectorAll('.quick-cart__items .quantity-input__input').forEach(input => {
            count += parseInt(input.value, 10) || 0;
        });
        return count;
    }

    setHTMLIfChanged(el, html) {
        if (!el || el._bh2oHtmlSignature === html) return;
        el.innerHTML = html;
        el._bh2oHtmlSignature = html;
    }

    parsePrice(str) {
        const match = String(str || '').match(/([\d,]+\.\d{2})/);
        return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
    }

    amountFromCents(value) {
        const cents = parseInt(value, 10);
        return Number.isFinite(cents) ? cents / 100 : null;
    }

    moneyFromTemplate(template, amount) {
        const source = /\d/.test(template || '') ? template : '$0.00';
        return source.replace(/([\d,]+\.\d{2})/, amount.toFixed(2)).replace(/\s*AUD/ig, '');
    }

    renderRewardMessage(template, amount, priceTemplate, fallback) {
        const source = template || fallback;
        return source.replace(/\[remainingForGoalFormatted\]/g, this.moneyFromTemplate(priceTemplate, amount));
    }

    isReturnsProtectionEnabled() {
        try {
            return window.localStorage.getItem(this.RETURNS_KEY) !== 'false';
        } catch (error) {
            return true;
        }
    }

    setReturnsProtectionEnabled(enabled) {
        try {
            window.localStorage.setItem(this.RETURNS_KEY, enabled ? 'true' : 'false');
        } catch (error) {
            return;
        }
    }

    // Where the customer actually is, not the market they are browsing.
    // Cached as "US|<timestamp>"; anything malformed or past the TTL is ignored.
    getDetectedCountry() {
        if (this.detectedCountry) return this.detectedCountry;
        try {
            const parts = String(window.localStorage.getItem(this.GEO_KEY) || '').split('|');
            const code = (parts[0] || '').toUpperCase();
            const stamp = parseInt(parts[1], 10);
            const fresh = Number.isFinite(stamp) && (Date.now() - stamp) < this.GEO_TTL;
            if (/^[A-Z]{2}$/.test(code) && fresh) {
                this.detectedCountry = code;
                return code;
            }
        } catch (error) {
            // storage blocked - fall back to the in-memory value
        }
        return null;
    }

    // Revalidated once per page load, so a cached country cannot outlive a real
    // change of location (travelling, or turning a VPN off).
    resolveDetectedCountry() {
        if (this.geoPending || this.geoChecked) return;
        if (typeof window.fetch !== 'function') return;

        this.geoChecked = true;
        this.geoPending = true;
        fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
            .then(response => (response.ok ? response.json() : null))
            .then(data => {
                const handle = data?.detected_values?.country?.handle;
                const code = String(handle || '').toUpperCase();
                if (!/^[A-Z]{2}$/.test(code)) return;

                const changed = this.getDetectedCountry() !== code;
                this.detectedCountry = code;
                try {
                    window.localStorage.setItem(this.GEO_KEY, code + '|' + Date.now());
                } catch (error) {
                    // storage blocked - the in-memory value still serves this session
                }
                // geoChecked is already set, so this redraw cannot recurse.
                if (changed) {
                    this.runMutations(this.observer);
                }
            })
            .catch(() => {
                // Detection unavailable - leave the bar showing.
            })
            .then(() => {
                this.geoPending = false;
            });
    }

    maybeResolveLocation() {
        const status = document.querySelector('[data-bh2o-reward-status]');
        if (status && status.dataset.hideBasis === 'location') {
            this.resolveDetectedCountry();
        }
    }

    shouldHideShippingBar(status) {
        if (!status) return false;
        if (status.dataset.hideBasis !== 'location') {
            return status.dataset.hideShippingBar === 'true';
        }

        const list = String(status.dataset.hideCountries || '')
            .toUpperCase()
            .split(',')
            .map(code => code.trim())
            .filter(Boolean);
        if (!list.length) return false;

        const country = this.getDetectedCountry();
        if (!country) return false;
        return list.indexOf(country) !== -1;
    }

    getShippingThreshold() {
        const status = document.querySelector('[data-bh2o-reward-status]');
        const thresholdCents = parseInt(status?.dataset.shippingThreshold, 10);
        return Number.isFinite(thresholdCents) ? thresholdCents / 100 : 160;
    }

    getShippingCost(total, shippingThreshold = this.getShippingThreshold()) {
        if (total >= shippingThreshold) return 0;
        const country = String(window.Shopify?.country || 'US').toUpperCase();
        if (country === 'AU') return 9.95;
        if (country === 'NZ') return 13.95;
        return 25.00;
    }

    ensureV2CartClass() {
        document.body.classList.add('is-test-active', 'test-3-variant-1');
    }

    updateRewardStatus(total, priceTemplate) {
        const status = document.querySelector('[data-bh2o-reward-status]');
        if (!status) return;

        const hideShippingBar = this.shouldHideShippingBar(status);
        const shippingThreshold = (parseInt(status.dataset.shippingThreshold, 10) || 16000) / 100;
        const giftThreshold = (parseInt(status.dataset.giftThreshold, 10) || 14900) / 100;
        const shippingUnlocked = total >= shippingThreshold;
        const giftUnlocked = total >= giftThreshold;
        const shippingLeft = Math.max(shippingThreshold - total, 0);
        const giftLeft = Math.max(giftThreshold - total, 0);

        const shippingHtml = hideShippingBar
            ? ''
            : (shippingUnlocked
                ? `<div class="bh2o-status-bubble bh2o-status-bubble--success">${status.dataset.shippingUnlockedMessage || '✓ Free shipping unlocked'}</div>`
                : `<div class="bh2o-status-bubble bh2o-status-bubble--alert">${this.renderRewardMessage(status.dataset.shippingProgressMessage, shippingLeft, priceTemplate, "🚚 You're [remainingForGoalFormatted] from FREE shipping")}</div>`);

        const giftHtml = `<div class="free_gifts_wrap free-gift-bubble ${giftUnlocked ? 'free-gift-bubble--unlocked' : 'free-gift-bubble--progress'}">
                <div class="free-gift-bubble__pill">
                    ${giftUnlocked ? (status.dataset.giftUnlockedMessage || '🎁 Free gift unlocked!') : this.renderRewardMessage(status.dataset.giftProgressMessage, giftLeft, priceTemplate, "🎁 You're [remainingForGoalFormatted] away from unlocking a free gift")}
                </div>
            </div>`;

        this.setHTMLIfChanged(status, `<div class="bh2o-status-wrap">${shippingHtml}</div>${giftHtml}`);
    }

    overrideShadowAndAppendStylesToRedo() {
        if (!Element.prototype._bh2oAttachShadowPatched) {
            const originalAttachShadow = Element.prototype.attachShadow;
            Element.prototype.attachShadow = function(init) {
                if (this.tagName === 'REDO-SHOPIFY-TOGGLE') {
                    return originalAttachShadow.call(this, { ...init, mode: 'open' });
                }
                return originalAttachShadow.call(this, init);
            };
            Element.prototype._bh2oAttachShadowPatched = true;
        }

        const el = document.querySelector('redo-shopify-toggle');
        if (!el) return;

        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
        el.style.setProperty('display', 'none', 'important');

        if (!el.shadowRoot || el.shadowRoot.querySelector('#bh2o-redo-style')) return;

        const style = document.createElement('style');
        style.id = 'bh2o-redo-style';
        style.textContent = `
            div[data-target="info-card-container"],
            div[data-target="checkout-buttons-container"] { margin: 0 !important; }
            button[data-target="coverage-button"] {
                background: #3A0A4D !important;
                color: #DCEB9A !important;
                border: 0 !important;
                border-radius: 10px !important;
                font-family: 'Cormorant Garamond', serif !important;
                font-size: 15px !important;
                font-weight: 500 !important;
                letter-spacing: 1px !important;
                text-transform: uppercase !important;
            }
            button[data-target="non-coverage-button"] { display: none !important; }
        `;
        el.shadowRoot.appendChild(style);

        const btn = el.shadowRoot.querySelector('button[data-target="coverage-button"]');
        if (btn) btn.innerText = 'SECURE CHECKOUT';
    }

    updateHeaderCount() {
        const totalCount = this.getCartCount();
        const title = document.querySelector('[data-v2-cart-title]');
        if (title) title.textContent = `Your Order (${totalCount})`;
        document.querySelectorAll('.quick-cart__header sup').forEach(sup => {
            sup.style.display = 'none';
        });
    }

    removeAUD() {
        document.querySelectorAll('.quick-cart__item-price, .a_pplr_item_oprice, .cross-sells__item-price, .cross-sells__item-price-wrapper').forEach(el => {
            el.childNodes.forEach(child => {
                if (child.nodeType === Node.TEXT_NODE && /AUD/i.test(child.textContent)) {
                    child.textContent = child.textContent.replace(/\s*AUD/ig, '');
                }
            });
        });
    }

    removeClutter() {
        document.querySelectorAll([
            '.bh2o-trustrow',
            '.bh2o-trust',
            '.bh2o-payments',
            '#bh2o-cart-extras',
            '.bh2o-savings',
            '.quick-cart__item-discounts',
            '.quick-cart__footer-order-note-wrapper',
            '.quick-cart__submit[name="checkout"]'
        ].join(',')).forEach(el => {
            el.style.display = 'none';
        });
    }

    formatPriceAndQty() {
        document.querySelectorAll('.quick-cart__item-details').forEach(details => {
            const priceWrap = details.querySelector('.quick-cart__item-price-wrapper');
            const qtyInput = details.querySelector('.quantity-input');

            if (priceWrap && qtyInput && !qtyInput.dataset.moved) {
                const flexRow = document.createElement('div');
                flexRow.className = 'custom-price-qty-row';
                priceWrap.before(flexRow);
                flexRow.appendChild(qtyInput);
                flexRow.appendChild(priceWrap);
                qtyInput.dataset.moved = 'true';
            }

            this.updateQuantityIcon(qtyInput);
        });
    }

    updateQuantityIcon(qtyInput) {
        if (!qtyInput) return;
        const inputEl = qtyInput.querySelector('.quantity-input__input');
        const minusBtn = qtyInput.querySelector('.product__quantity-subtract-item');
        if (!inputEl || !minusBtn) return;

        const qty = parseInt(inputEl.value, 10) || 1;
        if (qty <= 1 && minusBtn.dataset.bh2oTrashActive !== 'true') {
            minusBtn.dataset.bh2oTrashActive = 'true';
            minusBtn.classList.add('bh2o-quantity-remove');
            minusBtn.innerHTML = `
                <svg class="bh2o-trash-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                    <path d="M2.66699 5.09668H13.3337" stroke="#343233" stroke-width="1.19971" stroke-linecap="square" stroke-linejoin="round"></path>
                    <path d="M6.7207 7.84473V11.4365" stroke="#343233" stroke-width="1.19971" stroke-linecap="square" stroke-linejoin="round"></path>
                    <path d="M9.28027 7.84473V11.4365" stroke="#343233" stroke-width="1.19971" stroke-linecap="square" stroke-linejoin="round"></path>
                    <path d="M3.33398 5.09668C3.44653 6.44726 3.8724 10.015 4.15431 12.3276C4.30079 13.5292 5.32126 14.43 6.5318 14.43C7.48933 14.43 8.56834 14.43 9.48474 14.43C10.698 14.43 11.7202 13.5254 11.8637 12.3206C12.1391 10.0071 12.5551 6.44378 12.6673 5.09668" stroke="#343233" stroke-width="1.19971" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path d="M6 5.09684V3.09684C6 2.92003 6.07024 2.75046 6.19526 2.62544C6.32029 2.50042 6.48986 2.43018 6.66666 2.43018H9.33336C9.51016 2.43018 9.67968 2.50042 9.80472 2.62544C9.92976 2.75046 10 2.92003 10 3.09684V5.09684" stroke="#343233" stroke-width="1.19971" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>`;
        } else if (qty > 1 && minusBtn.dataset.bh2oTrashActive === 'true') {
            minusBtn.dataset.bh2oTrashActive = 'false';
            minusBtn.classList.remove('bh2o-quantity-remove');
            minusBtn.innerHTML = '<svg class="custom-minus-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"></path></svg>';
        }
    }

    buildBreakdown() {
        const oldSubtotalContainer = document.querySelector('.quick-cart__footer-subtotal');
        const cartItemsContainer = document.querySelector('.quick-cart__items');
        if (!oldSubtotalContainer || !cartItemsContainer) return;

        const countInt = this.getCartCount();
        const countStr = `${countInt} ${countInt === 1 ? 'item' : 'items'}`;

        let calculatedOriginalTotal = 0;
        let calculatedFinalTotal = 0;
        let stackEligibleCount = 0;
        let stackOriginalTotal = 0;
        let stackFinalTotal = 0;

        document.querySelectorAll('.quick-cart__item').forEach(item => {
            const qtyInput = item.querySelector('.quantity-input__input');
            const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
            const priceWrapper = item.querySelector('.quick-cart__item-price-wrapper');
            if (!priceWrapper) return;

            const originalPriceEl = priceWrapper.querySelector('s.quick-cart__item-price, s.a_pplr_item_oprice');
            const finalPriceEl = priceWrapper.querySelector('span.quick-cart__item-price');
            const itemFinalPrice = finalPriceEl ? this.parsePrice(finalPriceEl.textContent) : 0;
            const itemOriginalPrice = originalPriceEl ? this.parsePrice(originalPriceEl.textContent) : itemFinalPrice;
            const originalLineTotal = this.amountFromCents(item.dataset.originalLinePrice) ?? (itemOriginalPrice * qty);
            const finalLineTotal = this.amountFromCents(item.dataset.finalLinePrice) ?? (itemFinalPrice * qty);
            calculatedOriginalTotal += originalLineTotal;
            calculatedFinalTotal += finalLineTotal;

            if (item.dataset.stackSaveEligible === 'true') {
                stackEligibleCount += qty;
                stackOriginalTotal += originalLineTotal;
                stackFinalTotal += finalLineTotal;
            }
        });

        const stackSavingsVal = Math.max(stackOriginalTotal - stackFinalTotal, 0);
        const displaySubtotal = calculatedFinalTotal + stackSavingsVal;
        const savingsVal = stackSavingsVal;
        const shippingCost = this.getShippingCost(calculatedFinalTotal);
        const returnsProtectionFee = this.isReturnsProtectionEnabled() ? this.RETURNS_FEE : 0;
        const grandTotalOriginal = displaySubtotal + shippingCost + returnsProtectionFee;
        const grandTotalFinal = calculatedFinalTotal + shippingCost + returnsProtectionFee;

        const priceEl = oldSubtotalContainer.querySelector('.a_pplr_item_line_price, .pplr_subtotal_price');
        let fmtSrc = priceEl ? priceEl.textContent.trim() : '';
        if (!/\d/.test(fmtSrc)) {
            const anyItemPrice = document.querySelector('.quick-cart__item-price-wrapper .quick-cart__item-price, .quick-cart__item-price');
            fmtSrc = anyItemPrice ? anyItemPrice.textContent.trim() : '$0.00';
        }
        const priceTemplate = fmtSrc.replace(/\s*AUD/ig, '');
        this.updateRewardStatus(calculatedFinalTotal, priceTemplate);

        const subtotalStr = this.moneyFromTemplate(priceTemplate, displaySubtotal);
        const savingsStr = savingsVal > 0.01 ? `$${savingsVal.toFixed(2)}` : '';
        const shippingAndProtectionCost = shippingCost + returnsProtectionFee;
        const shippingAndProtectionLabel = returnsProtectionFee > 0
            ? 'Shipping + Package protection'
            : 'Shipping';
        const shippingAndProtectionStr = shippingAndProtectionCost === 0
            ? 'FREE'
            : this.moneyFromTemplate(priceTemplate, shippingAndProtectionCost);
        const finalOriginalStr = this.moneyFromTemplate(priceTemplate, grandTotalOriginal);
        const finalGrandTotalStr = this.moneyFromTemplate(priceTemplate, grandTotalFinal);

        if (priceEl) priceEl.style.display = 'none';
        const totalLabel = oldSubtotalContainer.querySelector('.ff-body.fs-body-200');
        if (totalLabel) totalLabel.innerText = 'Total';
        const nativeSubtotalValue = oldSubtotalContainer.querySelector(':scope > span.ff-body.fs-body-200');
        if (nativeSubtotalValue) nativeSubtotalValue.style.display = 'none';

        let customPriceUi = oldSubtotalContainer.querySelector('.custom-subtotal-price-ui');
        if (!customPriceUi) {
            customPriceUi = document.createElement('div');
            customPriceUi.className = 'custom-subtotal-price-ui';
            customPriceUi.style.display = 'flex';
            customPriceUi.style.alignItems = 'center';
            customPriceUi.style.justifyContent = 'flex-end';
            customPriceUi.style.gap = '7.5px';
            oldSubtotalContainer.appendChild(customPriceUi);
        }

        const customPriceHtml = savingsVal > 0.01
            ? `<s class="custom-old-price">${finalOriginalStr}</s><span class="custom-final-price">${finalGrandTotalStr}</span>`
            : `<span class="custom-final-price">${finalGrandTotalStr}</span>`;
        this.setHTMLIfChanged(customPriceUi, customPriceHtml);

        document.querySelectorAll('[data-bh2o-tierpill]').forEach(pill => {
            const activePercent = stackEligibleCount >= 3 ? 15 : stackEligibleCount >= 2 ? 10 : 0;
            const pillText = savingsVal > 0.01 && activePercent
                ? `✦ Stack & Save ${activePercent}% Off (-${savingsStr})`
                : '✦ Stack & Save';
            if (pill.textContent !== pillText) pill.textContent = pillText;
            if (savingsVal <= 0.01 || !activePercent) pill.closest('.bh2o-tierwrap')?.style.setProperty('display', 'none');
            else pill.closest('.bh2o-tierwrap')?.style.removeProperty('display');
        });

        let breakdown = document.querySelector('.custom-cart-breakdown');
        if (!breakdown) {
            breakdown = document.createElement('div');
            breakdown.className = 'custom-cart-breakdown';
            cartItemsContainer.appendChild(breakdown);
        }

        const breakdownHtml = `
            <div class="custom-breakdown-row custom-subtotal-row">
                <span class="custom-breakdown-label">Subtotal (<span class="custom-breakdown-count">${countStr}</span>)</span>
                <span class="custom-breakdown-value">${subtotalStr}</span>
            </div>
            ${savingsVal > 0.01 ? `
            <div class="custom-breakdown-row custom-savings-row">
                <span class="custom-breakdown-label">You Save</span>
                <span class="custom-breakdown-value">${savingsStr}</span>
            </div>` : ''}
            <div class="custom-breakdown-row custom-shipping-row">
                <span class="custom-breakdown-label">${shippingAndProtectionLabel}</span>
                <span class="custom-breakdown-value" ${shippingAndProtectionCost === 0 ? 'style="font-weight:600;color:#3A0A4D;"' : ''}>${shippingAndProtectionStr}</span>
            </div>
            <div class="custom-breakdown-row custom-total-row">
                <span class="custom-breakdown-label custom-total-label">Total</span>
                <span class="custom-breakdown-value custom-total-value" style="display:flex;align-items:center;gap:6px;">
                    <span class="custom-final-price">${finalGrandTotalStr}</span>
                </span>
            </div>`;
        this.setHTMLIfChanged(breakdown, breakdownHtml);
    }

    updateFooterPayments() {
        const taxNote = document.querySelector('.quick-cart__footer-tax-note');
        if (!taxNote || document.querySelector('.custom-payment-icons')) return;

        taxNote.style.display = 'none';
        const payments = document.createElement('div');
        payments.className = 'custom-payment-icons';
        const country = String(window.Shopify?.country || 'US').toUpperCase();
        const currency = String(window.Shopify?.currency?.active || window.Shopify?.currency?.current || window.Shopify?.currency || '').toUpperCase();
        const isAustraliaCart = country === 'AU' || currency === 'AUD';

        let iconsHtml = `
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/0ee6e1ebdc02f7ef5ca8dfcab9aa944afb618d43.png?v=1775083368" alt="Visa"></span>
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/bcb2246dcd36b5a7f1b57259e723258ec2c51d16.png?v=1775083368" alt="Mastercard"></span>
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/f19bc6720da56018f113bb71105a5c7a4cd1bb4a.png?v=1775083368" alt="American Express"></span>
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/5825ae72b8c9e704a09096666e6075306a0b833b.png?v=1775083368" alt="Apple Pay"></span>
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/c166d720c5d7057cdab9cfa5f8b3c3f691a34909.png?v=1775083368" alt="PayPal"></span>
            <span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/a3489c7df934756099e2f7435add6e7492c9740b.png?v=1775083368" alt="ShopPay"></span>`;
        if (isAustraliaCart) {
            iconsHtml += `<span class="custom-payment-icon"><img width="35" height="35" class="custom-payment-icon-img" src="https://cdn.shopify.com/s/files/1/0075/8061/4707/files/56388be1d5503d5b397927184d0d72e0f3a8d343.png?v=1775083368" alt="AfterPay"></span>`;
        }
        payments.innerHTML = iconsHtml;
        taxNote.after(payments);
    }

    buildCheckoutUI() {
        const footerInner = document.querySelector('.quick-cart__footer-inner');
        if (!footerInner) return;

        let ui = document.getElementById('bh2o-checkout-ui');
        if (!ui) {
            ui = document.createElement('div');
            ui.id = 'bh2o-checkout-ui';
            footerInner.appendChild(ui);
        }

        const enabled = this.isReturnsProtectionEnabled();
        const html = `
            <div id="bh2o-redo-row">
                <div class="lbl"><b>Free returns + package protection</b><span>Add for $${this.RETURNS_FEE.toFixed(2)}</span></div>
                <div id="bh2o-switch" role="switch" aria-checked="${enabled ? 'true' : 'false'}" tabindex="0" class="${enabled ? '' : 'off'}"><div class="knob"></div></div>
            </div>
            <button type="button" id="bh2o-checkout-btn">SECURE CHECKOUT</button>`;
        this.setHTMLIfChanged(ui, html);

        const toggle = ui.querySelector('#bh2o-switch');
        if (toggle && !toggle.dataset.bound) {
            const toggleProtection = () => {
                this.setReturnsProtectionEnabled(!this.isReturnsProtectionEnabled());
                this.runMutations(this.observer);
            };
            toggle.addEventListener('click', toggleProtection);
            toggle.addEventListener('keydown', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleProtection();
                }
            });
            toggle.dataset.bound = 'true';
        }

        const btn = ui.querySelector('#bh2o-checkout-btn');
        if (btn && !btn.dataset.bound) {
            btn.addEventListener('click', () => {
                const redoButton = document.querySelector('redo-shopify-toggle')?.shadowRoot?.querySelector('button[data-target="coverage-button"]');
                if (redoButton) {
                    redoButton.click();
                    return;
                }
                const nativeSubmit = document.querySelector('.quick-cart__submit[name="checkout"]');
                if (nativeSubmit) nativeSubmit.click();
            });
            btn.dataset.bound = 'true';
        }
    }

    runMutations(observer = null) {
        const activeObserver = observer || this.observer;
        if (activeObserver) activeObserver.disconnect();
        this.ensureV2CartClass();
        this.maybeResolveLocation();
        this.overrideShadowAndAppendStylesToRedo();
        this.updateHeaderCount();
        this.removeAUD();
        this.buildBreakdown();
        this.removeClutter();
        this.formatPriceAndQty();
        this.updateFooterPayments();
        this.buildCheckoutUI();
        if (activeObserver && this.observerTarget) activeObserver.observe(this.observerTarget, this.observerConfig);
    }

    debounce(fn, delay = 100) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    init() {
        if (!this.observerTarget) return;
        this.ensureV2CartClass();
        this.runMutations();

        const debouncedRun = this.debounce((observer) => {
            this.runMutations(observer);
        }, 100);

        this.observer = new MutationObserver((mutations, observer) => {
            if (mutations.some(mutation => ['childList', 'characterData', 'attributes'].includes(mutation.type))) {
                debouncedRun(observer);
            }
        });

        this.observer.observe(this.observerTarget, this.observerConfig);
        document.addEventListener('cart:updated', () => this.runMutations(this.observer));
        document.addEventListener('quick-cart:updated', () => this.runMutations(this.observer));
    }
}

if (document.querySelector('.quick-cart')) {
    new CartDrawerChanges();
}

(function() {
    var quickViewV2LayoutObserver = null;
    var quickViewV2LayoutFrame = null;

    function isQuickViewV2Page() {
        return document.body.classList.contains('template-collection-quickview-v2');
    }

    function getMediaItems(productEl) {
        return Array.from(productEl.querySelectorAll('.quick-product--left [data-product-media-wrapper]'));
    }

    function getActiveIndex(items) {
        var activeIndex = items.findIndex(function(item) {
            return !item.classList.contains('hidden');
        });

        return activeIndex > -1 ? activeIndex : 0;
    }

    function updateMediaArrows(productEl) {
        var items = getMediaItems(productEl);
        var previous = productEl.querySelector('[data-quick-product-v2-media-previous]');
        var next = productEl.querySelector('[data-quick-product-v2-media-next]');
        var activeIndex = getActiveIndex(items);

        if (previous) {
            previous.classList.toggle('quick-product-v2__media-arrow--visible', activeIndex > 0);
            previous.classList.toggle('quick-product-v2__media-arrow--hidden', activeIndex === 0);
            previous.setAttribute('aria-hidden', activeIndex === 0 ? 'true' : 'false');
            previous.tabIndex = activeIndex === 0 ? -1 : 0;
        }

        if (next) {
            next.classList.toggle('quick-product-v2__media-arrow--hidden', items.length < 2);
        }
    }

    function resetMediaToFirst(productEl) {
        var items = getMediaItems(productEl);
        if (!items.length || productEl.dataset.quickViewV2MediaInitialized === 'true') return;

        items.forEach(function(item, itemIndex) {
            item.classList.toggle('hidden', itemIndex !== 0);
        });
        productEl.dataset.quickViewV2MediaInitialized = 'true';
    }

    function showMedia(productEl, index) {
        var items = getMediaItems(productEl);
        if (!items.length) return;

        var nextIndex = (index + items.length) % items.length;
        items.forEach(function(item, itemIndex) {
            item.classList.toggle('hidden', itemIndex !== nextIndex);
        });

        updateMediaArrows(productEl);
    }

    function bindQuickViewV2Media() {
        if (!isQuickViewV2Page()) return;

        document.querySelectorAll('.quick-view-modal__content .quick-product').forEach(function(productEl) {
            if (productEl.dataset.quickViewV2MediaBound === 'true') return;

            var previous = productEl.querySelector('[data-quick-product-v2-media-previous]');
            var next = productEl.querySelector('[data-quick-product-v2-media-next]');
            if (!previous && !next) return;

            productEl.dataset.quickViewV2MediaBound = 'true';
            resetMediaToFirst(productEl);
            updateMediaArrows(productEl);
        });
    }

    function assignQuickViewV2FormOwner(gpoContainer, form, index) {
        if (!gpoContainer || !form || form.tagName !== 'FORM') return;

        var formId = form.getAttribute('id');
        if (!formId) {
            formId = 'quick-view-v2-product-form-' + index;
            form.setAttribute('id', formId);
        }

        gpoContainer.querySelectorAll('input, select, textarea').forEach(function(field) {
            if (!field.name) return;
            field.setAttribute('form', formId);
        });
    }

    function moveQuickViewV2GpoToScrollableContent() {
        if (!isQuickViewV2Page()) return;

        document.querySelectorAll('.quick-view-modal__content .quick-product').forEach(function(productEl, productIndex) {
            var footer = productEl.querySelector('.quick-product--right-viewport--footer');
            var inner = productEl.querySelector('.quick-product--right-inner');
            if (!footer || !inner) return;

            footer.querySelectorAll('.gpo-container').forEach(function(gpoContainer, gpoIndex) {
                var form = gpoContainer.closest('form.product-form') || gpoContainer.closest('form[data-product-form]');
                if (!form) return;

                assignQuickViewV2FormOwner(gpoContainer, form, productIndex + '-' + gpoIndex);
                gpoContainer.dataset.quickViewV2ScrollableGpo = 'true';
                productEl.classList.add('quick-product-v2--scrollable-gpo');
                if (footer.querySelector('.gpo-clone-atc-button')) {
                    productEl.classList.add('quick-product-v2--has-gpo-clone-atc');
                }
                if (gpoContainer.parentElement !== inner) {
                    inner.appendChild(gpoContainer);
                }
            });
        });
    }

    function requestQuickViewV2Layout() {
        if (quickViewV2LayoutFrame) return;

        quickViewV2LayoutFrame = requestAnimationFrame(function() {
            quickViewV2LayoutFrame = null;
            moveQuickViewV2GpoToScrollableContent();
        });
    }

    function observeQuickViewV2Layout() {
        if (!isQuickViewV2Page() || quickViewV2LayoutObserver) return;

        quickViewV2LayoutObserver = new MutationObserver(requestQuickViewV2Layout);
        quickViewV2LayoutObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    function scheduleQuickViewV2Layout() {
        if (!isQuickViewV2Page()) return;

        observeQuickViewV2Layout();
        moveQuickViewV2GpoToScrollableContent();
        window.setTimeout(moveQuickViewV2GpoToScrollableContent, 100);
        window.setTimeout(moveQuickViewV2GpoToScrollableContent, 500);
        window.setTimeout(moveQuickViewV2GpoToScrollableContent, 1500);
        window.setTimeout(moveQuickViewV2GpoToScrollableContent, 3000);
    }

    document.addEventListener('click', function(event) {
        if (!isQuickViewV2Page()) return;

        var previous = event.target.closest('[data-quick-product-v2-media-previous]');
        var next = event.target.closest('[data-quick-product-v2-media-next]');
        if (!previous && !next) return;

        var productEl = event.target.closest('.quick-product');
        if (!productEl) return;

        event.preventDefault();
        event.stopPropagation();

        var items = getMediaItems(productEl);
        var offset = next ? 1 : -1;
        showMedia(productEl, getActiveIndex(items) + offset);
    });

    document.addEventListener('quickview:loaded', function() {
        bindQuickViewV2Media();
        scheduleQuickViewV2Layout();
    });
    document.addEventListener('DOMContentLoaded', function() {
        bindQuickViewV2Media();
        scheduleQuickViewV2Layout();
    });
})();

(function() {
    function isSwiperCardCollectionPage() {
        return document.body.classList.contains('template-collection-swiper-card');
    }

    function updateSwipeIndex(card) {
        var viewport = card.querySelector('[data-card-image-swipe-viewport]');
        var dots = Array.from(card.querySelectorAll('[data-card-image-swipe-dot]')).slice(0, 3);
        if (!viewport || !dots.length) return;

        var slideWidth = viewport.clientWidth || 1;
        var activeIndex = Math.round(viewport.scrollLeft / slideWidth);
        activeIndex = Math.max(0, Math.min(activeIndex, dots.length - 1));

        dots.forEach(function(dot, index) {
            dot.setAttribute('data-active', index === activeIndex ? 'true' : 'false');
        });
    }

    function showSwipeImage(card, index) {
        var viewport = card.querySelector('[data-card-image-swipe-viewport]');
        if (!viewport) return;

        viewport.scrollTo({
            left: viewport.clientWidth * index,
            behavior: 'smooth'
        });
        requestAnimationFrame(function() {
            updateSwipeIndex(card);
        });
    }

    function bindSwipeIndex(card) {
        if (card.dataset.bh2oSwipeIndexBound === 'true') {
            updateSwipeIndex(card);
            return;
        }

        var viewport = card.querySelector('[data-card-image-swipe-viewport]');
        if (!viewport) return;

        var frame = null;
        var requestUpdate = function() {
            if (frame) return;
            frame = requestAnimationFrame(function() {
                frame = null;
                updateSwipeIndex(card);
            });
        };

        card.dataset.bh2oSwipeIndexBound = 'true';
        Array.from(card.querySelectorAll('[data-card-image-swipe-dot]')).slice(0, 3).forEach(function(dot, index) {
            dot.setAttribute('role', 'button');
            dot.setAttribute('tabindex', '0');
            dot.setAttribute('aria-label', 'Show image ' + (index + 1));
            dot.addEventListener('click', function(event) {
                event.preventDefault();
                event.stopPropagation();
                showSwipeImage(card, index);
            });
            dot.addEventListener('keydown', function(event) {
                if (event.key !== 'Enter' && event.key !== ' ') return;

                event.preventDefault();
                event.stopPropagation();
                showSwipeImage(card, index);
            });
        });
        viewport.addEventListener('scroll', requestUpdate, { passive: true });
        viewport.addEventListener('touchend', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);
        updateSwipeIndex(card);
    }

    function bindSwiperCardIndexes() {
        if (!isSwiperCardCollectionPage()) return;

        document.querySelectorAll('[data-card-image-swipe]').forEach(bindSwipeIndex);
    }

    document.addEventListener('DOMContentLoaded', bindSwiperCardIndexes);
    document.addEventListener('collection:updated', bindSwiperCardIndexes);
    document.addEventListener('shopify:section:load', bindSwiperCardIndexes);
})();

(function() {
    function isStickyCollectionPage() {
        return document.body.classList.contains('template-collection-sticky');
    }

    function setCollectionStickyHeights() {
        if (!isStickyCollectionPage()) return;

        var banner = document.querySelector('[data-collection-sticky-banner="true"]');
        var circleMenu = document.querySelector('[data-collection-sticky-circle-menu="true"]');
        var bannerHeight = banner ? banner.offsetHeight : 0;
        var circleMenuHeight = circleMenu ? circleMenu.offsetHeight : 0;

        document.body.style.setProperty('--collection-sticky-banner-height', bannerHeight + 'px');
        document.body.style.setProperty('--collection-sticky-circle-menu-height', circleMenuHeight + 'px');
    }

    function initCollectionStickyStack() {
        if (!isStickyCollectionPage()) return;

        setCollectionStickyHeights();
        window.addEventListener('resize', setCollectionStickyHeights);
        window.addEventListener('load', setCollectionStickyHeights);

        if ('ResizeObserver' in window) {
            var observer = new ResizeObserver(setCollectionStickyHeights);
            document.querySelectorAll('[data-collection-sticky-banner="true"], [data-collection-sticky-circle-menu="true"]').forEach(function(el) {
                observer.observe(el);
            });
        }
    }

    document.addEventListener('DOMContentLoaded', initCollectionStickyStack);
    document.addEventListener('shopify:section:load', setCollectionStickyHeights);
})();

(function() {
    // Toggles `.is-pinned` on the sticky collection banner when it actually pins to
    // the top, so CSS can collapse the breadcrumbs and shrink the title. position:sticky
    // has no CSS "stuck" state, so we watch a zero-height sentinel placed just above the
    // banner (in normal flow, so it scrolls): once its top reaches the top offset, the
    // banner has pinned. A rAF-throttled scroll check keeps this cheap and deterministic.
    function isStickyCollectionPage() {
        return document.body.classList.contains('template-collection-sticky');
    }

    function captureBannerTitleBaseline(banner) {
        // Capture the UNPINNED heading size so the pinned 70%/50% math has a stable
        // baseline. Never read while pinned, or the reduced value ratchets down.
        if (banner.classList.contains('is-pinned')) return;
        var heading = banner.querySelector('.collection-banner__text-container-heading');
        if (!heading) return;
        var cs = getComputedStyle(heading);
        banner.style.setProperty('--ctitle-font', cs.fontSize);
        banner.style.setProperty('--ctitle-mb', cs.marginBottom);
    }

    function topOffsetPx() {
        var v = getComputedStyle(document.body).getPropertyValue('--collection-sticky-top-offset').trim();
        var n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    }

    // Module-level so repeated shopify:section:load (theme editor) rebinds rather than
    // stacking window listeners on detached closures.
    var boundScroll = null;
    var boundResize = null;

    function initStickyBannerPinnedState() {
        if (!isStickyCollectionPage()) return;
        var banner = document.querySelector('.collection-banner--mobile-sticky-top');
        if (!banner) return;
        var wrapper = banner.closest('.shopify-section') || banner.parentElement;
        if (!wrapper || !wrapper.parentElement) return;

        captureBannerTitleBaseline(banner);

        var sentinel = wrapper.previousElementSibling;
        if (!sentinel || !sentinel.classList.contains('collection-sticky-banner-sentinel')) {
            sentinel = document.createElement('div');
            sentinel.className = 'collection-sticky-banner-sentinel';
            sentinel.setAttribute('aria-hidden', 'true');
            sentinel.style.cssText = 'height:0;margin:0;padding:0;pointer-events:none;';
            wrapper.parentElement.insertBefore(sentinel, wrapper);
        }

        var ticking = false;
        function update() {
            ticking = false;
            var pinned = sentinel.getBoundingClientRect().top <= topOffsetPx() + 0.5;
            banner.classList.toggle('is-pinned', pinned);
        }

        if (boundScroll) window.removeEventListener('scroll', boundScroll);
        if (boundResize) window.removeEventListener('resize', boundResize);

        boundScroll = function() {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
        };
        boundResize = function() {
            captureBannerTitleBaseline(banner);
            boundScroll();
        };

        update();
        window.addEventListener('scroll', boundScroll, { passive: true });
        window.addEventListener('resize', boundResize);
    }

    document.addEventListener('DOMContentLoaded', initStickyBannerPinnedState);
    document.addEventListener('shopify:section:load', initStickyBannerPinnedState);
})();

(function() {
    var stickyHeaderObserver = null;
    var stickyHeaderFrame = null;

    function isStickyCollectionPage() {
        return document.body.classList.contains('template-collection-sticky');
    }

    function disableStickyHeader() {
        if (!isStickyCollectionPage()) return;

        if (document.documentElement.classList.contains('sticky-header-enabled')) {
            document.documentElement.classList.remove('sticky-header-enabled');
        }
        if (document.documentElement.classList.contains('sticky-header-active')) {
            document.documentElement.classList.remove('sticky-header-active');
        }
        document.documentElement.style.setProperty('--header-desktop-sticky-position', '0px');
        document.documentElement.style.setProperty('--header-desktop-sticky-height', '0px');
        document.documentElement.style.setProperty('--mobile-sticky-header-height', '0px');

        document.querySelectorAll('.header').forEach(function(header) {
            if (header.hasAttribute('data-enable-sticky-header')) {
                header.removeAttribute('data-enable-sticky-header');
            }
            if (header.hasAttribute('data-is-sticky')) {
                header.removeAttribute('data-is-sticky');
            }
            if (header.classList.contains('is-sticky')) {
                header.classList.remove('is-sticky');
            }
        });
    }

    function scheduleDisableStickyHeader() {
        if (stickyHeaderFrame) return;

        stickyHeaderFrame = requestAnimationFrame(function() {
            stickyHeaderFrame = null;
            disableStickyHeader();
        });
    }

    function initDisableStickyHeader() {
        if (!isStickyCollectionPage()) return;
        if (stickyHeaderObserver) {
            scheduleDisableStickyHeader();
            return;
        }

        disableStickyHeader();

        stickyHeaderObserver = new MutationObserver(scheduleDisableStickyHeader);
        stickyHeaderObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        document.querySelectorAll('.header').forEach(function(header) {
            stickyHeaderObserver.observe(header, {
                attributes: true,
                attributeFilter: ['class', 'data-enable-sticky-header', 'data-is-sticky']
            });
        });
    }

    disableStickyHeader();
    document.addEventListener('DOMContentLoaded', initDisableStickyHeader);
    document.addEventListener('shopify:section:load', initDisableStickyHeader);
    window.addEventListener('load', disableStickyHeader);
})();
